import {
  Container,
  Grid,
  Box,
  Slide,
  Fade,
  Grow,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { useState, useEffect, useRef } from "react";
import Axios from "axios";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";

import { Loading } from "../../components/shared/loading";
import { TypingQuote } from "../../components/shared/TypingQuote";
import { HeroParticles } from "../../components/shared/HeroParticles";
import { resolveIcon } from "../../data/productIcons";

const headKey = "qweqeasdj123n1jasndjn2j3njsdn12i891bdjsdu1283djsd1231231";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "product", label: "Products" },
  { key: "saas", label: "SaaS" },
  { key: "game", label: "Games" },
];
// Category group for a product (falls back sensibly for older data).
const groupOf = (p) =>
  p.group ||
  (p.status === "saas" ? "saas" : p.status === "soon" ? "game" : "product");

const useStyles = makeStyles((theme) => ({
  "@keyframes heroFadeUp": {
    from: { opacity: 0, transform: "translateY(24px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  "@keyframes heroExpand": {
    from: { width: 0, opacity: 0 },
    to: { width: 90, opacity: 1 },
  },
  "@keyframes heroShimmerSweep": {
    "0%": { backgroundPosition: "-200% center" },
    "100%": { backgroundPosition: "200% center" },
  },
  "@keyframes orbDrift": {
    "0%, 100%": { transform: "translate(0, 0) scale(1)" },
    "50%": { transform: "translate(14px, -12px) scale(1.12)" },
  },
  "@keyframes orbDriftAlt": {
    "0%, 100%": { transform: "translate(0, 0) scale(1)" },
    "50%": { transform: "translate(-16px, 10px) scale(1.08)" },
  },
  "@keyframes dotPulse": {
    "0%, 100%": { boxShadow: "0 0 0 0 rgba(124,252,155,0.6)" },
    "50%": { boxShadow: "0 0 0 5px rgba(124,252,155,0)" },
  },
  "@keyframes dotPulseSoon": {
    "0%, 100%": { boxShadow: "0 0 0 0 rgba(242,193,78,0.6)" },
    "50%": { boxShadow: "0 0 0 5px rgba(242,193,78,0)" },
  },

  // ── Hero ──
  heroSection: {
    position: "relative",
    overflow: "hidden",
    minHeight: "45vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background:
      "linear-gradient(135deg, #1a001a 0%, #2d002d 50%, #1a001a 100%)",
  },
  heroContent: {
    position: "relative",
    zIndex: 1,
    textAlign: "center",
    color: "#ffffff",
    padding: "40px 20px",
    maxWidth: 720,
  },
  heroTitle: {
    fontSize: "clamp(1.8rem, 4vw, 2.5rem)",
    fontWeight: 700,
    marginBottom: 10,
    backgroundImage:
      "linear-gradient(90deg, #ffffff 0%, #e0b8e0 25%, #c77dc7 50%, #e0b8e0 75%, #ffffff 100%)",
    backgroundSize: "200% auto",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    color: "transparent",
    animation:
      "$heroFadeUp 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.15s both, $heroShimmerSweep 5s linear infinite",
  },
  heroDivider: {
    width: 90,
    height: 3,
    background:
      "linear-gradient(90deg, transparent, #c77dc7 30%, #e0b8e0 50%, #c77dc7 70%, transparent)",
    margin: "16px auto",
    borderRadius: 2,
    animation: "$heroExpand 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.55s both",
  },
  heroSubtitle: {
    fontSize: "clamp(0.85rem, 1.5vw, 1rem)",
    fontWeight: 400,
    opacity: 0.9,
    maxWidth: 540,
    margin: "0 auto",
    lineHeight: 1.6,
    animation: "$heroFadeUp 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.5s both",
  },

  // ── Section ──
  section: {
    padding: "56px 0 44px",
    background: "#fafafa",
  },
  sectionTitle: {
    fontSize: "1.4rem",
    fontWeight: 600,
    color: "#4c004c",
    textAlign: "center",
    marginBottom: 8,
  },
  sectionSubtitle: {
    fontSize: "0.85rem",
    color: "#666666",
    textAlign: "center",
    maxWidth: 500,
    margin: "0 auto 40px",
    lineHeight: 1.6,
  },
  groupTitle: {
    fontSize: "1.05rem",
    fontWeight: 700,
    color: "#4c004c",
    textAlign: "center",
    marginBottom: 6,
  },
  groupSubtitle: {
    fontSize: "0.8rem",
    color: "#777777",
    textAlign: "center",
    maxWidth: 460,
    margin: "0 auto 26px",
    lineHeight: 1.6,
  },
  groupBlock: {
    marginTop: 44,
  },
  filterRow: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: 10,
    margin: "10px 0 38px",
  },
  filterPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: "0.85rem",
    fontWeight: 600,
    color: "#4c004c",
    background: "#ffffff",
    border: "1px solid rgba(76,0,76,0.16)",
    borderRadius: 30,
    padding: "9px 18px",
    transition: "all 0.28s cubic-bezier(0.22,1,0.36,1)",
    "&:hover": {
      borderColor: "rgba(76,0,76,0.4)",
      transform: "translateY(-2px)",
      boxShadow: "0 8px 20px rgba(76,0,76,0.12)",
    },
  },
  filterCount: {
    fontSize: "0.7rem",
    fontWeight: 700,
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 6px",
    background: "rgba(76,0,76,0.08)",
    color: "#9b3b9b",
  },
  filterPillActive: {
    color: "#ffffff",
    background: "linear-gradient(135deg, #6b1b6b 0%, #4c004c 100%)",
    borderColor: "transparent",
    boxShadow: "0 10px 24px rgba(76,0,76,0.32)",
    "&:hover": { color: "#ffffff", transform: "translateY(-2px)" },
    "& $filterCount": {
      background: "rgba(255,255,255,0.22)",
      color: "#ffffff",
    },
  },
  emptyNote: {
    textAlign: "center",
    color: "#888888",
    fontSize: "0.95rem",
    padding: "44px 0",
  },

  // ── Product card ──
  cardLink: {
    position: "relative",
    display: "block",
    height: "100%",
    textDecoration: "none",
    "&, &:hover, &:focus, &:active, &:visited": {
      textDecoration: "none",
    },
    "&:hover $cardGlow": {
      opacity: 0.55,
    },
  },
  cardGlow: {
    position: "absolute",
    inset: -3,
    borderRadius: 26,
    filter: "blur(22px)",
    opacity: 0,
    zIndex: 0,
    transition: "opacity 0.45s cubic-bezier(0.22, 1, 0.36, 1)",
    pointerEvents: "none",
  },
  card: {
    position: "relative",
    zIndex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    background: "#ffffff",
    borderRadius: 22,
    overflow: "hidden",
    border: "1px solid rgba(76,0,76,0.07)",
    boxShadow: "0 6px 24px rgba(0,0,0,0.05)",
    transformStyle: "preserve-3d",
    transition:
      "transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.4s ease, border-color 0.4s ease",
    "&:hover": {
      boxShadow: "0 30px 60px rgba(76,0,76,0.22)",
      borderColor: "rgba(76,0,76,0.14)",
    },
    "&:hover $spotlight": { opacity: 1 },
    "&:hover $shine": { transform: "translateX(240%) skewX(-18deg)" },
    "&:hover $cardMarkTile": {
      transform: "translateZ(30px) scale(1.08) rotate(-4deg)",
    },
    "&:hover $cardArrowWrap": {
      background: "#ffffff",
      borderColor: "#ffffff",
    },
    "&:hover $cardArrow": {
      transform: "translateX(3px)",
      color: "#4c004c",
    },
    "&:hover $chip": {
      transform: "translateY(-2px)",
    },
  },
  spotlight: {
    position: "absolute",
    inset: 0,
    zIndex: 3,
    opacity: 0,
    transition: "opacity 0.4s ease",
    pointerEvents: "none",
    background:
      "radial-gradient(260px circle at var(--mx, 50%) var(--my, 0%), rgba(199,125,199,0.16), transparent 60%)",
  },
  cardHeader: {
    position: "relative",
    padding: "30px 26px 26px",
    overflow: "hidden",
  },
  orb: {
    position: "absolute",
    borderRadius: "50%",
    pointerEvents: "none",
  },
  orbOne: {
    width: 150,
    height: 150,
    top: -70,
    right: -40,
    background:
      "radial-gradient(circle, rgba(255,255,255,0.22), transparent 68%)",
    animation: "$orbDrift 7s ease-in-out infinite",
  },
  orbTwo: {
    width: 120,
    height: 120,
    bottom: -55,
    left: -30,
    background:
      "radial-gradient(circle, rgba(255,255,255,0.14), transparent 68%)",
    animation: "$orbDriftAlt 9s ease-in-out infinite",
  },
  shine: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "45%",
    height: "100%",
    background:
      "linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)",
    transform: "translateX(-220%) skewX(-18deg)",
    transition: "transform 0.85s cubic-bezier(0.22, 1, 0.36, 1)",
    pointerEvents: "none",
    zIndex: 2,
  },
  cardHeaderTop: {
    position: "relative",
    zIndex: 3,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
  cardMarkTile: {
    width: 60,
    height: 60,
    borderRadius: 16,
    background: "rgba(255,255,255,0.15)",
    border: "1px solid rgba(255,255,255,0.28)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#ffffff",
    boxShadow: "0 10px 26px rgba(0,0,0,0.22)",
    transition: "transform 0.45s cubic-bezier(0.22, 1, 0.36, 1)",
    "& svg": { fontSize: 32 },
  },
  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: "0.6rem",
    fontWeight: 700,
    letterSpacing: "1px",
    textTransform: "uppercase",
    padding: "5px 11px",
    borderRadius: 20,
    color: "#ffffff",
    background: "rgba(255,255,255,0.16)",
    border: "1px solid rgba(255,255,255,0.3)",
    backdropFilter: "blur(4px)",
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "#7CFC9B",
    animation: "$dotPulse 2.2s ease-in-out infinite",
  },
  statusDotSoon: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "#f2c14e",
    animation: "$dotPulseSoon 2.2s ease-in-out infinite",
  },
  cardName: {
    position: "relative",
    zIndex: 3,
    color: "#ffffff",
    fontSize: "1.45rem",
    fontWeight: 700,
    marginTop: 20,
    letterSpacing: "-0.01em",
  },
  cardExpansion: {
    position: "relative",
    zIndex: 3,
    color: "rgba(255,255,255,0.85)",
    fontSize: "0.8rem",
    fontWeight: 400,
    marginTop: 4,
    lineHeight: 1.45,
  },

  cardBody: {
    position: "relative",
    zIndex: 3,
    flex: 1,
    display: "flex",
    flexDirection: "column",
    padding: "24px 26px 26px",
  },
  categoryPill: {
    alignSelf: "flex-start",
    fontSize: "0.6rem",
    fontWeight: 700,
    color: "#9b3b9b",
    textTransform: "uppercase",
    letterSpacing: "1.4px",
    background:
      "linear-gradient(135deg, rgba(199,125,199,0.15), rgba(76,0,76,0.05))",
    border: "1px solid rgba(76,0,76,0.1)",
    borderRadius: 20,
    padding: "5px 12px",
    marginBottom: 14,
  },
  cardSummary: {
    fontSize: "0.85rem",
    color: "#555",
    lineHeight: 1.7,
    marginBottom: 20,
  },
  chipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 24,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: "0.7rem",
    fontWeight: 500,
    color: "#4c004c",
    background: "#f6eef6",
    borderRadius: 9,
    padding: "6px 10px",
    transition: "transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), background 0.3s ease",
    "& svg": { fontSize: 14, color: "#9b3b9b" },
  },
  cardFooter: {
    marginTop: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },
  footerText: {
    fontSize: "0.85rem",
    fontWeight: 600,
    color: "#4c004c",
  },
  cardArrowWrap: {
    width: 38,
    height: 38,
    minWidth: 38,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid rgba(76,0,76,0.2)",
    background: "transparent",
    transition: "all 0.35s cubic-bezier(0.22, 1, 0.36, 1)",
  },
  cardArrow: {
    fontSize: 18,
    color: "#4c004c",
    transition: "all 0.35s cubic-bezier(0.22, 1, 0.36, 1)",
  },

  // ── Founder quote ──
  quoteWrap: {
    background: "#ffffff",
    padding: "12px 0 64px",
  },
  quoteSection: {
    position: "relative",
    background: "linear-gradient(135deg, #6b1b6b 0%, #4a0e4a 100%)",
    borderRadius: 16,
    padding: "36px 28px",
    textAlign: "center",
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      width: 280,
      height: 280,
      borderRadius: "50%",
      top: -140,
      right: -100,
      background:
        "radial-gradient(circle, rgba(199,125,199,0.28), transparent 70%)",
      pointerEvents: "none",
    },
  },
  quoteText: {
    position: "relative",
    zIndex: 1,
    fontSize: "0.95rem",
    color: "#ffffff",
    lineHeight: 1.7,
    fontStyle: "italic",
    maxWidth: 720,
    margin: "0 auto 14px",
  },
  quoteAuthor: {
    position: "relative",
    zIndex: 1,
    color: "#ffffff",
    fontSize: "0.8rem",
    fontWeight: 600,
    opacity: 0.9,
  },
}));

// Individual card, extracted so each one owns its cursor-follow tilt + spotlight.
const ProductCard = ({ product, index, classes }) => {
  const Icon = resolveIcon(product.icon);
  const tiltRef = useRef(null);

  const available =
    product.status === "live" || product.status === "saas";
  const statusText =
    product.status === "live"
      ? "Live"
      : product.status === "saas"
      ? "Available"
      : "Coming soon";

  const accentGradient = `linear-gradient(135deg, ${product.accent.from} 0%, ${product.accent.to} 100%)`;

  const handleMove = (e) => {
    const el = tiltRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width;
    const y = (e.clientY - r.top) / r.height;
    el.style.setProperty("--mx", `${x * 100}%`);
    el.style.setProperty("--my", `${y * 100}%`);
    const rotY = (x - 0.5) * 8;
    const rotX = (0.5 - y) * 8;
    el.style.transform = `perspective(1100px) rotateX(${rotX}deg) rotateY(${rotY}deg) translateY(-6px) scale(1.015)`;
  };

  const handleLeave = () => {
    const el = tiltRef.current;
    if (el) el.style.transform = "";
  };

  return (
    <Grow in={true} timeout={700 + index * 140}>
      <Link to={`/product/${product.slug}`} className={classes.cardLink}>
        <Box className={classes.cardGlow} style={{ background: accentGradient }} />
        <Box
          ref={tiltRef}
          className={classes.card}
          onMouseMove={handleMove}
          onMouseLeave={handleLeave}
        >
          <Box className={classes.spotlight} />

          {/* Header */}
          <Box className={classes.cardHeader} style={{ background: accentGradient }}>
            <Box className={`${classes.orb} ${classes.orbOne}`} />
            <Box className={`${classes.orb} ${classes.orbTwo}`} />
            <Box className={classes.shine} />
            <Box className={classes.cardHeaderTop}>
              <Box
                className={classes.cardMarkTile}
                style={product.logo ? { background: "#ffffff", padding: 4 } : undefined}
              >
                {product.logo ? (
                  <img
                    src={product.logo}
                    alt={product.name}
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                  />
                ) : (
                  <Icon />
                )}
              </Box>
              <Box className={classes.statusPill}>
                <span
                  className={available ? classes.statusDot : classes.statusDotSoon}
                />
                {statusText}
              </Box>
            </Box>
            <Typography className={classes.cardName}>{product.name}</Typography>
            <Typography className={classes.cardExpansion}>
              {product.expansion}
            </Typography>
          </Box>

          {/* Body */}
          <Box className={classes.cardBody}>
            <Typography className={classes.categoryPill}>
              {product.category}
            </Typography>
            <Typography className={classes.cardSummary}>
              {product.listSummary}
            </Typography>
            <Box className={classes.chipRow}>
              {product.highlights.map((h, i) => {
                const HIcon = resolveIcon(h.icon);
                return (
                  <span className={classes.chip} key={i}>
                    <HIcon />
                    {h.label}
                  </span>
                );
              })}
            </Box>
            <Box className={classes.cardFooter}>
              <span className={classes.footerText}>
                View {product.name}
              </span>
              <Box className={classes.cardArrowWrap}>
                <ArrowForwardIcon className={classes.cardArrow} />
              </Box>
            </Box>
          </Box>
        </Box>
      </Link>
    </Grow>
  );
};

export const Products = () => {
  const classes = useStyles();

  const [metaTitle, setMetaTitle] = useState("");
  const [metaDesc, setMetaDesc] = useState("");
  const [metaKeywords, setMetaKeywords] = useState("");
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [isloading, setIsloading] = useState(true);

  const handleClick = () => {
    const anchor = document.querySelector("#back-to-top-anchor");
    if (anchor) {
      anchor.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  useEffect(() => {
    getMetaData();
    getProducts();
  }, []);

  const getProducts = () => {
    Axios.get("/api/products")
      .then((res) => {
        if (res.data && res.data.status) {
          setProducts(res.data.data || []);
        }
      })
      .catch(() => {});
  };

  const getMetaData = () => {
    const formData = new FormData();
    formData.append("page", 3);
    Axios.post("/api/getmetadata", formData, {
      headers: { headKey: headKey },
    })
      .then((res) => {
        if (res.data.status) {
          setMetaTitle(res.data.data.meta_title);
          setMetaKeywords(res.data.data.meta_keywords);
          setMetaDesc(res.data.data.meta_desc);
        }
      })
      .finally(() => {
        setIsloading(false);
        handleClick();
      });
  };

  return (
    <div>
      <Helmet>
        <title>{metaTitle || "Our Products | Arawinz Soft Solutions"}</title>
        <meta name="description" content={metaDesc} />
        <meta name="keywords" content={metaKeywords}></meta>
      </Helmet>

      {/* Hero Section */}
      <Box className={classes.heroSection}>
        <HeroParticles id="products-hero-particles" />
        <Fade in={true} timeout={1000}>
          <Box className={classes.heroContent}>
            <Typography className={classes.heroTitle}>Our Products</Typography>
            <Box className={classes.heroDivider} />
            <Typography className={classes.heroSubtitle}>
              Software we build for real work. Pick a product to see what it
              does and how to get it for your team.
            </Typography>
          </Box>
        </Fade>
      </Box>

      {isloading ? (
        <Loading />
      ) : (
        <>
          <Box className={classes.section}>
            <Container maxWidth="lg">
              <Slide direction="up" in={true} timeout={800}>
                <Box>
                  <Typography className={classes.sectionTitle}>
                    Built by Arawinz, made for real work
                  </Typography>
                  <Typography className={classes.sectionSubtitle}>
                    Products, SaaS tools and games from the same team. Filter by
                    what you are after, and open any one to see more.
                  </Typography>
                </Box>
              </Slide>

              {/* Category filters */}
              <Box className={classes.filterRow}>
                {FILTERS.map((f) => {
                  const count =
                    f.key === "all"
                      ? products.length
                      : products.filter((p) => groupOf(p) === f.key).length;
                  return (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => setFilter(f.key)}
                      className={`${classes.filterPill} ${
                        filter === f.key ? classes.filterPillActive : ""
                      }`}
                    >
                      {f.label}
                      <span className={classes.filterCount}>{count}</span>
                    </button>
                  );
                })}
              </Box>

              {(() => {
                const shown =
                  filter === "all"
                    ? products
                    : products.filter((p) => groupOf(p) === filter);
                if (shown.length === 0) {
                  return (
                    <Typography className={classes.emptyNote}>
                      Nothing here yet. Coming soon.
                    </Typography>
                  );
                }
                return (
                  <Grid container spacing={4} alignItems="stretch">
                    {shown.map((product, index) => (
                      <Grid item xs={12} sm={6} key={product.slug}>
                        <ProductCard
                          product={product}
                          index={index}
                          classes={classes}
                        />
                      </Grid>
                    ))}
                  </Grid>
                );
              })()}
            </Container>
          </Box>

          {/* Founder quote */}
          <Box className={classes.quoteWrap}>
            <Container maxWidth="lg">
              <Fade in={true} timeout={1200}>
                <Box className={classes.quoteSection}>
                  <TypingQuote
                    text={`"At Arawinz, we try to build what an industry actually needs. Every product here started with a real problem someone had."`}
                    className={classes.quoteText}
                  />
                  <Typography className={classes.quoteAuthor}>
                    - Chethan. P. S. S, Managing Director
                  </Typography>
                </Box>
              </Fade>
            </Container>
          </Box>
        </>
      )}
    </div>
  );
};
